import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClientDetailResponse, MonitorData } from '../types/client';
import MonitorTab from './MonitorTab';
import { api } from '../api';
import { fmtEur, fmtNum } from '../utils/format';
import StatsChart from './StatsChart';
import Topbar from './ui/Topbar';
import BackButton from './ui/BackButton';
import Badge from './ui/Badge';
import Tabs from './ui/Tabs';
import SingleSelect from './ui/SingleSelect';

const SYNC_TABS = new Set(['soporte', 'ventas', 'ventasApp', 'parametros', 'connector']);

const ClientDetail: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [client, setClient] = useState<ClientDetailResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.getClient(id)
            .then((data) => { if (!cancelled) setClient(data); })
            .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Error al cargar cliente'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [id]);

    const currentYear = new Date().getFullYear();

    const [tab, setTab] = useState<'general' | 'backoffice' | 'licencias' | 'soporte' | 'ventas' | 'ventasApp' | 'parametros' | 'connector'>('general');
    const [monitorData, setMonitorData] = useState<MonitorData | null>(null);
    const [monitorLoading, setMonitorLoading] = useState(false);
    const [paramSearch, setParamSearch] = useState<string>('');
    const [b2bYear, setB2bYear] = useState<string>('');
    const [b2bMonth, setB2bMonth] = useState<string>('');
    const [b2bCompareYear, setB2bCompareYear] = useState<string>('');
    const [b2bCompareMonth, setB2bCompareMonth] = useState<string>('');

    const [appYear, setAppYear] = useState<string>('');
    const [appMonth, setAppMonth] = useState<string>('');
    const [appCompareYear, setAppCompareYear] = useState<string>('');
    const [appCompareMonth, setAppCompareMonth] = useState<string>('');

    const syncing = Boolean(
        (client?.soporte as any)?.sync_status === 'syncing' ||
        (client?.ventas as any)?.sync_status === 'syncing' ||
        client?.connector?.sync_status === 'syncing'
    );
    const syncingRef = useRef(false);
    syncingRef.current = syncing;

    useEffect(() => {
        if (!id || !syncingRef.current || !SYNC_TABS.has(tab)) return;
        let cancelled = false;
        setRefreshing(true);
        api.getClient(id)
            .then((data) => { if (!cancelled) setClient(data); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setRefreshing(false); });
        return () => { cancelled = true; };
    }, [tab, id]);

    const activeParams = useMemo(() => {
        if (!client) return [];
        const active = (client.parametros ?? [])
            .filter((p) => {
                const v = String(p.datvalor ?? '').trim().toLowerCase();
                return v === '1' || v === 'true' || v === 'yes' || v === 'si';
            })
            .map((p) => p.codparametro);
        return active.sort((a, b) => a.localeCompare(b, 'es'));
    }, [client]);

    const filteredActiveParams = useMemo(() => {
        const q = paramSearch.trim().toLowerCase();
        if (!q) return activeParams;
        return activeParams.filter((p) => p.toLowerCase().includes(q));
    }, [activeParams, paramSearch]);

    const isCloud = !!client?.infraestructura;

    useEffect(() => {
        if (tab !== 'backoffice' || !isCloud || !id || monitorData) return;
        let cancelled = false;
        setMonitorLoading(true);
        api.getClientMonitor(id)
            .then((data) => { if (!cancelled) setMonitorData(data); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setMonitorLoading(false); });
        return () => { cancelled = true; };
    }, [tab, id, isCloud, monitorData]);

    const b2bResumen = useMemo(() => {
        return (client?.ventas as any)?.b2b?.resumen as Record<string, unknown> | undefined;
    }, [client]);

    const appResumen = useMemo(() => {
        return (client?.ventas as any)?.app?.resumen as Record<string, unknown> | undefined;
    }, [client]);

    const b2bMensual = useMemo(() => (client?.ventas as any)?.b2b?.mensual ?? [], [client]);
    const appMensual = useMemo(() => (client?.ventas as any)?.app?.mensual ?? [], [client]);

    const b2bYears = useMemo(() => {
        const ys = new Set<number>();
        for (const r of b2bMensual) {
            const y = Number((r.fecmes as string).slice(0, 4));
            if (!Number.isNaN(y)) ys.add(y);
        }
        const arr = Array.from(ys).sort((a, b) => b - a);
        return arr.length === 0 ? Array.from({ length: 20 }, (_, i) => currentYear - i) : arr;
    }, [b2bMensual, currentYear]);

    const appYears = useMemo(() => {
        const ys = new Set<number>();
        for (const r of appMensual) {
            const y = Number((r.fecmes as string).slice(0, 4));
            if (!Number.isNaN(y)) ys.add(y);
        }
        const arr = Array.from(ys).sort((a, b) => b - a);
        return arr.length === 0 ? Array.from({ length: 20 }, (_, i) => currentYear - i) : arr;
    }, [appMensual, currentYear]);

    const months = [
        { value: '', label: 'Todos' },
        { value: '01', label: 'Enero' },
        { value: '02', label: 'Febrero' },
        { value: '03', label: 'Marzo' },
        { value: '04', label: 'Abril' },
        { value: '05', label: 'Mayo' },
        { value: '06', label: 'Junio' },
        { value: '07', label: 'Julio' },
        { value: '08', label: 'Agosto' },
        { value: '09', label: 'Septiembre' },
        { value: '10', label: 'Octubre' },
        { value: '11', label: 'Noviembre' },
        { value: '12', label: 'Diciembre' },
    ];

    const calcTotals = (rows: any[]) =>
        rows.reduce(
            (acc, r) => ({
                pedidos: acc.pedidos + (r.total_pedidos ?? 0),
                lineas: acc.lineas + (r.total_lineas ?? 0),
                importeNeto: acc.importeNeto + (r.importe_neto_eur ?? 0),
                importeTotal: acc.importeTotal + (r.importe_total_eur ?? 0),
            }),
            { pedidos: 0, lineas: 0, importeNeto: 0, importeTotal: 0 }
        );

    const filterRows = (rows: any[], year: string, month: string) =>
        rows.filter((r) => {
            if (year && !(r.fecmes as string).startsWith(`${year}-`)) return false;
            if (month && (r.fecmes as string).slice(5, 7) !== month) return false;
            return true;
        });

    const b2bTotals = useMemo(
        () => calcTotals(filterRows(b2bMensual, b2bYear, b2bMonth)),
        [b2bMensual, b2bYear, b2bMonth]
    );

    const b2bCompareEnabled = Boolean(b2bCompareYear || b2bCompareMonth);

    const b2bCompareTotals = useMemo(() => {
        const y = b2bCompareYear || b2bYear;
        const m = b2bCompareMonth || b2bMonth;
        return calcTotals(filterRows(b2bMensual, y, m));
    }, [b2bMensual, b2bYear, b2bMonth, b2bCompareYear, b2bCompareMonth]);

    const b2bChartMain = useMemo(
        () => (b2bYear ? b2bMensual.filter((r: any) => r.fecmes.startsWith(`${b2bYear}-`)) : b2bMensual),
        [b2bMensual, b2bYear]
    );

    const b2bChartCompare = useMemo(() => {
        if (!b2bCompareEnabled) return null;
        if (!b2bYear) return b2bMensual;
        const effYear = b2bCompareYear || b2bYear;
        return b2bMensual.filter((r: any) => r.fecmes.startsWith(`${effYear}-`));
    }, [b2bCompareEnabled, b2bCompareYear, b2bMensual, b2bYear]);

    const appTotals = useMemo(
        () => calcTotals(filterRows(appMensual, appYear, appMonth)),
        [appMensual, appYear, appMonth]
    );

    const appCompareEnabled = Boolean(appCompareYear || appCompareMonth);

    const appCompareTotals = useMemo(() => {
        const y = appCompareYear || appYear;
        const m = appCompareMonth || appMonth;
        return calcTotals(filterRows(appMensual, y, m));
    }, [appMensual, appYear, appMonth, appCompareYear, appCompareMonth]);

    const appChartMain = useMemo(
        () => (appYear ? appMensual.filter((r: any) => r.fecmes.startsWith(`${appYear}-`)) : appMensual),
        [appMensual, appYear]
    );

    const appChartCompare = useMemo(() => {
        if (!appCompareEnabled) return null;
        if (!appYear) return appMensual;
        const effYear = appCompareYear || appYear;
        return appMensual.filter((r: any) => r.fecmes.startsWith(`${effYear}-`));
    }, [appCompareEnabled, appCompareYear, appMensual, appYear]);

    if (loading || !client) {
        return (
            <div id="detail-screen" className="screen active">
                <Topbar
                    title=""
                    left={
                        <BackButton onClick={() => navigate('/')}>
                            <span className="icon-s icon-atras" />
                            Volver al listado
                        </BackButton>
                    }
                />
                <div className="detail-header">
                    <div className="detail-name">
                        {loading ? 'Cargando…' : error ? error : 'Cliente no encontrado'}
                    </div>
                </div>
            </div>
        );
    }

    const soporte = client.soporte as any;

    return (
        <div id="detail-screen" className="screen active">
            <Topbar
                title=""
                left={
                    <BackButton onClick={() => navigate('/')}>
                        <span className="icon-s icon-atras" />
                        Volver al listado
                    </BackButton>
                }
            />

            <div className="detail-header">
                <div className="detail-title-row">
                    <div className="detail-name" id="detail-name">{client.general.nombre}</div>
                    {client.general.clasificacion ? <Badge variant={client.general.clasificacion}>{client.general.clasificacion.toUpperCase()}</Badge> : null}
                </div>
                <div className="detail-meta">
                    <span>
                        Código: <strong>{client.general.codigocliente}</strong>
                    </span>
                    {client.general.estado ? <Badge variant={client.general.estado}>{client.general.estado}</Badge> : null}
                </div>
            </div>

            <Tabs
                items={[
                    { id: 'general', label: (<><span className="icon-s icon-carpeta" />General</>) },
                    { id: 'backoffice', label: (<><span className="icon-s icon-bd" />BackOffice</>) },
                    { id: 'licencias', label: (<><span className="icon-s icon-movil" />Licencias</>) },
                    { id: 'soporte', label: (<><span className="icon-s icon-soporte" />Soporte</>) },
                    ...(client.general.tiene_b2b ? [
                        { id: 'ventas', label: (<><span className="icon-s icon-carritolleno" />B2B</>) },
                    ] : []),
                    { id: 'ventasApp', label: (<><span className="icon-s icon-movil" />APP</>) },
                    { id: 'parametros', label: (<><span className="icon-s icon-opciones" />Parámetros</>) },
                    ...(client.infraestructura ? [
                        { id: 'connector', label: (<><span className="icon-s icon-conector" />Connector</>) },
                    ] : []),
                ]}
                value={tab}
                onChange={(v) => setTab(v as typeof tab)}
            />
            {refreshing && SYNC_TABS.has(tab) && (
                <div className="tab-sync-notice">Actualizando datos…</div>
            )}

            <div className={tab === 'general' ? 'tab-content active' : 'tab-content'}>
                <div className="contact-list">
                    <div className="contact-row">
                        <span className="icon-s icon-carpeta" />
                        <div className="contact-label">Razón social</div>
                        <div className="contact-value">{client.general.razonsocial ?? '—'}</div>
                    </div>
                    <div className="contact-row">
                        <span className="icon-s icon-tag" />
                        <div className="contact-label">Tipo</div>
                        <div className="contact-value">{client.general.tipo_cliente ?? '—'}</div>
                    </div>
                    <div className="contact-row">
                        <span className="icon-s icon-location" />
                        <div className="contact-label">Población</div>
                        <div className="contact-value">{client.general.poblacion ?? '—'}</div>
                    </div>
                    <div className="contact-row">
                        <span className="icon-s icon-location" />
                        <div className="contact-label">Provincia</div>
                        <div className="contact-value">{client.general.provincia ?? '—'}</div>
                    </div>
                    <div className="contact-row">
                        <span className="icon-s icon-location" />
                        <div className="contact-label">País</div>
                        <div className="contact-value">{client.general.pais ?? '—'}</div>
                    </div>
                    <div className="contact-row">
                        <span className="icon-s icon-backoffice" />
                        <div className="contact-label">Sector</div>
                        <div className="contact-value">{client.general.sector ?? '—'}</div>
                    </div>
                    <div className="contact-row">
                        <span className="icon-s icon-backoffice" />
                        <div className="contact-label">Subsector</div>
                        <div className="contact-value">{client.general.subsector ?? '—'}</div>
                    </div>
                    <div className="contact-row">
                        <span className="icon-s icon-user" />
                        <div className="contact-label">Agente</div>
                        <div className="contact-value">{client.general.nombre_agente ?? '—'}</div>
                    </div>
                    <div className="contact-row">
                        <span className="icon-s icon-calendario" />
                        <div className="contact-label">Fecha alta</div>
                        <div className="contact-value">{client.general.fechaalta?.slice(0, 10) ?? '—'}</div>
                    </div>
                    <div className="contact-row">
                        <span className="icon-s icon-calendario" />
                        <div className="contact-label">Fecha baja</div>
                        <div className="contact-value">{client.general.fechabaja?.slice(0, 10) ?? '—'}</div>
                    </div>
                    {client.general.motivobaja ? (
                        <div className="contact-row">
                            <span className="icon-s icon-discard" />
                            <div className="contact-label">Motivo baja</div>
                            <div className="contact-value">{client.general.motivobaja}</div>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className={tab === 'backoffice' ? 'tab-content active' : 'tab-content'}>
                {client.infraestructura ? (
                    <>
                        <div className="section-title">OVH</div>
                        <div className="field-grid field-grid-3">
                            <div className="field"><div className="field-label">URL</div><div className="field-value">{client.infraestructura.subdominio && client.infraestructura.domain ? `${client.infraestructura.subdominio}.${client.infraestructura.domain}` : '—'}</div></div>
                            <div className="field"><div className="field-label">Plan</div><div className="field-value">{client.infraestructura.plancode ? client.infraestructura.plancode.split('.')[0] : '—'}</div></div>
                            <div className="field"><div className="field-label">Región</div><div className="field-value">{client.infraestructura.region ?? '—'}</div></div>
                        </div>
                        {client.infraestructura.volumenes?.length > 0 && (
                            <>
                                <div className="section-title">Volúmenes</div>
                                <div className="table-scroll-wrap">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Nombre</th>
                                                <th>Tipo</th>
                                                <th className="col-num">Tamaño</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {client.infraestructura.volumenes.map((v) => (
                                                <tr key={v.name}>
                                                    <td>{v.name}</td>
                                                    <td>{v.type}</td>
                                                    <td className="col-num">{v.size}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {monitorLoading && <div className="tab-sync-notice">Cargando analítica…</div>}
                        {monitorData && <MonitorTab data={monitorData} />}
                    </>
                ) : (
                    <div className="tab-empty-state">Cliente on-premise</div>
                )}
            </div>

            <div className={tab === 'licencias' ? 'tab-content active' : 'tab-content'}>
                {client.licencias.length > 0 ? (
                    <>
                        <div className="metric-grid">
                            <div className="metric">
                                <div className="metric-label">Total licencias</div>
                                <div className="metric-value">{client.licencias.length}</div>
                            </div>
                            <div className="metric">
                                <div className="metric-label">Compradas</div>
                                <div className="metric-value">{client.licencias.reduce((s, l) => s + l.licenciascompradas, 0)}</div>
                            </div>
                            <div className="metric">
                                <div className="metric-label">Usadas</div>
                                <div className="metric-value">{client.licencias.reduce((s, l) => s + l.licenciasusadas, 0)}</div>
                            </div>
                            <div className="metric">
                                <div className="metric-label">Vivas</div>
                                <div className="metric-value">{client.licencias.reduce((s, l) => s + l.licenciasvivas, 0)}</div>
                            </div>
                            <div className="metric">
                                <div className="metric-label">Windows</div>
                                <div className="metric-value">{client.licencias.reduce((s, l) => s + l.totalwindows, 0)}</div>
                            </div>
                            <div className="metric">
                                <div className="metric-label">iOS</div>
                                <div className="metric-value">{client.licencias.reduce((s, l) => s + l.totalios, 0)}</div>
                            </div>
                            <div className="metric">
                                <div className="metric-label">Android</div>
                                <div className="metric-value">{client.licencias.reduce((s, l) => s + l.totalandroid, 0)}</div>
                            </div>
                        </div>
                        <div className="table-scroll-wrap">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Descripción</th>
                                        <th>Estatus</th>
                                        <th className="col-num">Compradas</th>
                                        <th className="col-num">Usadas</th>
                                        <th className="col-num">Vivas</th>
                                        <th>Alta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {client.licencias.map((lic) => (
                                        <tr key={lic.idventa}>
                                            <td>{lic.desarticulo}</td>
                                            <td><span className={`lic-badge lic-badge--${lic.estatus}`}>{lic.estatus}</span></td>
                                            <td className="col-num">{lic.licenciascompradas}</td>
                                            <td className="col-num">{lic.licenciasusadas}</td>
                                            <td className="col-num">{lic.licenciasvivas}</td>
                                            <td className="col-date">{lic.fechaalta?.slice(0, 10) ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <div className="tab-empty-state">Sin licencias registradas</div>
                )}
            </div>

            <div className={tab === 'soporte' ? 'tab-content active' : 'tab-content'}>
                {soporte ? (
                    <div className="metric-grid metric-grid-5">
                        <div className="metric"><div className="metric-label">Total tickets</div><div className="metric-value">{soporte.tickets_total ?? '—'}</div></div>
                        <div className="metric"><div className="metric-label">Abiertos</div><div className="metric-value text-warning">{soporte.tickets_abiertos ?? '—'}</div></div>
                        <div className="metric"><div className="metric-label">Resueltos</div><div className="metric-value">{soporte.tickets_resueltos ?? '—'}</div></div>
                        <div className="metric"><div className="metric-label">Recientes</div><div className="metric-value">{soporte.tickets_recientes ?? '—'}</div></div>
                        <div className="metric"><div className="metric-label">Último ticket</div><div className="metric-value" style={{ fontSize: 14 }}>{String(soporte.ultimo_ticket_at ?? '—').slice(0, 10)}</div></div>
                    </div>
                ) : (
                    <div className="tab-empty-state">Sin datos de soporte</div>
                )}
            </div>

            <div className={tab === 'ventas' ? 'tab-content active' : 'tab-content'}>
                {!b2bResumen && b2bMensual.length === 0 ? (
                    <div className="tab-empty-state">Este cliente no tiene datos de B2B</div>
                ) : (
                    <>
                        <div className="period-tabs">
                            {[
                                { label: 'Histórico', year: '' },
                                { label: String(currentYear), year: String(currentYear) },
                                { label: String(currentYear - 1), year: String(currentYear - 1) },
                            ].map((p) => (
                                <div
                                    key={p.label}
                                    className={b2bYear === p.year ? 'period-tab active' : 'period-tab'}
                                    onClick={() => { setB2bYear(p.year); setB2bMonth(''); setB2bCompareYear(''); setB2bCompareMonth(''); }}
                                >
                                    {p.label}
                                </div>
                            ))}
                        </div>

                        <div className="filters-row">
                            <div className="filters-group">
                                <div className="filters-item">
                                    <div className="field-label">Año</div>
                                    <SingleSelect
                                        value={b2bYear}
                                        onChange={setB2bYear}
                                        options={[{ value: '', label: 'Todos' }, ...b2bYears.map((y) => ({ value: String(y), label: String(y) }))]}
                                    />
                                </div>
                                <div className="filters-item">
                                    <div className="field-label">Mes</div>
                                    <SingleSelect value={b2bMonth} onChange={setB2bMonth} options={months} />
                                </div>
                            </div>
                            <div className="filters-group">
                                <div className="filters-item">
                                    <div className="field-label">Año comparativo</div>
                                    <SingleSelect
                                        value={b2bCompareYear}
                                        onChange={setB2bCompareYear}
                                        options={[{ value: '', label: 'Ninguno' }, ...b2bYears.map((y) => ({ value: String(y), label: String(y) }))]}
                                    />
                                </div>
                                <div className="filters-item">
                                    <div className="field-label">Mes comparativo</div>
                                    <SingleSelect
                                        value={b2bCompareMonth}
                                        onChange={setB2bCompareMonth}
                                        options={[{ value: '', label: 'Ninguno' }, ...months.filter((m) => m.value !== '')]}
                                    />
                                </div>
                            </div>
                        </div>

                        <StatsChart
                            main={b2bChartMain}
                            compare={b2bChartCompare}
                            yearMode={b2bYear !== ''}
                            mainLabel={b2bYear || 'Histórico'}
                            compareLabel={b2bCompareYear || b2bYear || 'Comparativo'}
                            metrics={['importe_total_eur', 'total_pedidos', 'total_lineas']}
                        />

                        <div className="section-title">Resumen del período</div>
                        <div className={`metric-grid metric-grid-${isCloud ? 4 : 3}`}>
                            <div className="metric"><div className="metric-label">Pedidos</div><div className="metric-value">{fmtNum(b2bTotals.pedidos)}</div></div>
                            <div className="metric"><div className="metric-label">Líneas</div><div className="metric-value">{fmtNum(b2bTotals.lineas)}</div></div>
                            {isCloud && <div className="metric"><div className="metric-label">Importe neto</div><div className="metric-value">{fmtEur(b2bTotals.importeNeto)}</div></div>}
                            <div className="metric"><div className="metric-label">Importe total</div><div className="metric-value">{fmtEur(b2bTotals.importeTotal)}</div></div>
                        </div>

                        {b2bCompareEnabled ? (
                            <>
                                <div className="section-title">Resumen comparativo</div>
                                <div className={`metric-grid metric-grid-${isCloud ? 4 : 3}`}>
                                    <div className="metric"><div className="metric-label">Pedidos</div><div className="metric-value">{fmtNum(b2bCompareTotals.pedidos)}</div></div>
                                    <div className="metric"><div className="metric-label">Líneas</div><div className="metric-value">{fmtNum(b2bCompareTotals.lineas)}</div></div>
                                    {isCloud && <div className="metric"><div className="metric-label">Importe neto</div><div className="metric-value">{fmtEur(b2bCompareTotals.importeNeto)}</div></div>}
                                    <div className="metric"><div className="metric-label">Importe total</div><div className="metric-value">{fmtEur(b2bCompareTotals.importeTotal)}</div></div>
                                </div>
                            </>
                        ) : null}

                        {b2bResumen && (
                            <div className="field-grid">
                                <div className="field"><div className="field-label">Primer pedido</div><div className="field-value">{(b2bResumen as any)?.hist_primer_pedido ?? '—'}</div></div>
                                <div className="field"><div className="field-label">Último pedido</div><div className="field-value">{(b2bResumen as any)?.hist_ultimo_pedido ?? '—'}</div></div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className={tab === 'ventasApp' ? 'tab-content active' : 'tab-content'}>
                {!appResumen && appMensual.length === 0 ? (
                    <div className="tab-empty-state">Este cliente no tiene datos de APP</div>
                ) : (
                    <>
                        <div className="period-tabs">
                            {[
                                { label: 'Histórico', year: '' },
                                { label: String(currentYear), year: String(currentYear) },
                                { label: String(currentYear - 1), year: String(currentYear - 1) },
                            ].map((p) => (
                                <div
                                    key={p.label}
                                    className={appYear === p.year ? 'period-tab active' : 'period-tab'}
                                    onClick={() => { setAppYear(p.year); setAppMonth(''); setAppCompareYear(''); setAppCompareMonth(''); }}
                                >
                                    {p.label}
                                </div>
                            ))}
                        </div>

                        <div className="filters-row">
                            <div className="filters-group">
                                <div className="filters-item">
                                    <div className="field-label">Año</div>
                                    <SingleSelect
                                        value={appYear}
                                        onChange={setAppYear}
                                        options={[{ value: '', label: 'Todos' }, ...appYears.map((y) => ({ value: String(y), label: String(y) }))]}
                                    />
                                </div>
                                <div className="filters-item">
                                    <div className="field-label">Mes</div>
                                    <SingleSelect value={appMonth} onChange={setAppMonth} options={months} />
                                </div>
                            </div>
                            <div className="filters-group">
                                <div className="filters-item">
                                    <div className="field-label">Año comparativo</div>
                                    <SingleSelect
                                        value={appCompareYear}
                                        onChange={setAppCompareYear}
                                        options={[{ value: '', label: 'Ninguno' }, ...appYears.map((y) => ({ value: String(y), label: String(y) }))]}
                                    />
                                </div>
                                <div className="filters-item">
                                    <div className="field-label">Mes comparativo</div>
                                    <SingleSelect
                                        value={appCompareMonth}
                                        onChange={setAppCompareMonth}
                                        options={[{ value: '', label: 'Ninguno' }, ...months.filter((m) => m.value !== '')]}
                                    />
                                </div>
                            </div>
                        </div>

                        <StatsChart
                            main={appChartMain}
                            compare={appChartCompare}
                            yearMode={appYear !== ''}
                            mainLabel={appYear || 'Histórico'}
                            compareLabel={appCompareYear || appYear || 'Comparativo'}
                            metrics={['importe_total_eur', 'total_pedidos', 'total_lineas']}
                        />

                        <div className="section-title">Resumen del período</div>
                        <div className={`metric-grid metric-grid-${isCloud ? 4 : 3}`}>
                            <div className="metric"><div className="metric-label">Pedidos</div><div className="metric-value">{fmtNum(appTotals.pedidos)}</div></div>
                            <div className="metric"><div className="metric-label">Líneas</div><div className="metric-value">{fmtNum(appTotals.lineas)}</div></div>
                            {isCloud && <div className="metric"><div className="metric-label">Importe neto</div><div className="metric-value">{fmtEur(appTotals.importeNeto)}</div></div>}
                            <div className="metric"><div className="metric-label">Importe total</div><div className="metric-value">{fmtEur(appTotals.importeTotal)}</div></div>
                        </div>

                        {appCompareEnabled ? (
                            <>
                                <div className="section-title">Resumen comparativo</div>
                                <div className={`metric-grid metric-grid-${isCloud ? 4 : 3}`}>
                                    <div className="metric"><div className="metric-label">Pedidos</div><div className="metric-value">{fmtNum(appCompareTotals.pedidos)}</div></div>
                                    <div className="metric"><div className="metric-label">Líneas</div><div className="metric-value">{fmtNum(appCompareTotals.lineas)}</div></div>
                                    {isCloud && <div className="metric"><div className="metric-label">Importe neto</div><div className="metric-value">{fmtEur(appCompareTotals.importeNeto)}</div></div>}
                                    <div className="metric"><div className="metric-label">Importe total</div><div className="metric-value">{fmtEur(appCompareTotals.importeTotal)}</div></div>
                                </div>
                            </>
                        ) : null}

                        {appResumen && (
                            <div className="field-grid">
                                <div className="field"><div className="field-label">Primer pedido</div><div className="field-value">{(appResumen as any)?.hist_primer_pedido ?? '—'}</div></div>
                                <div className="field"><div className="field-label">Último pedido</div><div className="field-value">{(appResumen as any)?.hist_ultimo_pedido ?? '—'}</div></div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className={tab === 'connector' ? 'tab-content active' : 'tab-content'}>
                {client.connector ? (
                    <>
                        <div className="field-grid field-grid-3">
                            <div className="field">
                                <div className="field-label">Versión InaConnector</div>
                                <div className="field-value">{client.connector.version ?? '—'}</div>
                            </div>
                            <div className="field">
                                <div className="field-label">gRPC</div>
                                <div className="field-value">
                                    {client.connector.grpc_connected === null ? '—'
                                        : client.connector.grpc_connected ? 'Conectado' : 'Desconectado'}
                                </div>
                            </div>
                            <div className="field">
                                <div className="field-label">Versión cliente</div>
                                <div className="field-value">{client.connector.grpc_client_version ?? '—'}</div>
                            </div>
                        </div>

                        {client.connector.configs.length > 0 ? (
                            <>
                                <div className="section-title">Configuraciones</div>
                                <div className="table-scroll-wrap">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Nombre</th>
                                                <th>Tipo</th>
                                                <th>Modo</th>
                                                <th>Último lanzamiento</th>
                                                <th>Duración</th>
                                                <th>Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {client.connector.configs.map((c) => (
                                                <tr key={c.name}>
                                                    <td>{c.name}</td>
                                                    <td>{c.type ?? '—'}</td>
                                                    <td>{c.mode ?? '—'}</td>
                                                    <td>{c.last_launch ?? '—'}</td>
                                                    <td>{c.last_duration ?? '—'}</td>
                                                    <td>
                                                        {c.has_error === null ? '—'
                                                            : c.has_error
                                                                ? <span className="lic-badge lic-badge--error">Error</span>
                                                                : <span className="lic-badge lic-badge--Activo">OK</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="tab-empty-state">Sin configuraciones</div>
                        )}
                    </>
                ) : (
                    <div className="tab-empty-state">Sin datos del connector</div>
                )}
            </div>

            <div className={tab === 'parametros' ? 'tab-content active' : 'tab-content'}>
                <div className="metric params-table-card">
                    <table>
                        <thead>
                            <tr>
                                <th>
                                    <div className="table-search">
                                        <input
                                            className="ui-input ui-input-search"
                                            value={paramSearch}
                                            onChange={(e) => setParamSearch(e.target.value)}
                                            placeholder="Buscar parámetro activo"
                                        />
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredActiveParams.length === 0 ? (
                                <tr><td className="text-secondary">—</td></tr>
                            ) : (
                                filteredActiveParams.map((p) => (
                                    <tr key={p}><td>{p}</td></tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ClientDetail;
